import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Container,
  Row,
  Col,
  Table,
  Button,
  Collapse,
  Form,
} from "react-bootstrap";
import PageTitle from "../components/PageTitle";
import { Calender, DateFormat, DropDown } from "../components/Forms";
import { Buttons } from "../components/Buttons";
import {
  fetchGroupedBalanceSheet,
  deleteBalanceSheet,
} from "../slices/balanceSheetSlice";
import { fetchAgent } from "../slices/agentSlice";
import NotifyData from "../components/NotifyData";
import Pagnation from "../components/Pagnation";
import { useNavigate } from "react-router-dom";
import Dialog from "../components/Dialog";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";

const BalanceSheet = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { groupedBalanceSheet, totals } = useSelector(
    (state) => state.balanceSheet
  );
  const { agent } = useSelector((state) => state.agent);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [nameFilter, setNameFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("All");
  const [openRows, setOpenRows] = useState(new Set());
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedItemToDelete, setSelectedItemToDelete] = useState(null);

  const showCredit = typeFilter === "All" || typeFilter === "Credit";
  const showDebit = typeFilter === "All" || typeFilter === "Debit";
  const mainCols = 2 + (showCredit ? 1 : 0) + (showDebit ? 1 : 0) + 1;
  const detailsCols =
    1 +
    (showCredit ? 1 : 0) +
    (showDebit ? 1 : 0) +
    (showCredit || showDebit ? 1 : 0); // Action only if credit/debit shown

  useEffect(() => {
    dispatch(fetchGroupedBalanceSheet({ fromDate, toDate, nameFilter }));
  }, [dispatch, fromDate, toDate, nameFilter]);

  useEffect(() => {
    dispatch(fetchAgent());
  }, [dispatch]);

  const handleAdd = () => {
    navigate("/create-balance-sheet");
  };

  const handleEdit = (detail) => {
    if (detail.type !== "credit") {
      NotifyData("Edit is only available for credit entries!", "error");
      return;
    }
    const editData = {
      balance_sheet_id: detail.balance_sheet_id,
      entry_date: detail.entry_date,
      agent_name: detail.agent_name,
      agent_id: detail.agent_id,
      amount: detail.credit,
      description: detail.description,
      type: detail.type,
    };
    navigate("/create-balance-sheet", {
      state: { isEdit: true, data: editData },
    });
  };

  const handleDelete = (detail) => {
    if (detail.type !== "credit") {
      NotifyData("Delete is only available for credit entries!", "error");
      return;
    }
    setSelectedItemToDelete(detail);
    setShowDeleteDialog(true);
  };

  const confirmDelete = async () => {
    if (selectedItemToDelete) {
      try {
        await dispatch(
          deleteBalanceSheet(selectedItemToDelete.balance_sheet_id)
        ).unwrap();
        NotifyData("Balance sheet entry deleted successfully!", "success");
        // Refetch after delete
        dispatch(fetchGroupedBalanceSheet({ fromDate, toDate, nameFilter }));
      } catch (error) {
        NotifyData(
          "Failed to delete balance sheet entry. Please try again!",
          "error"
        );
        console.error(error);
      }
    }
    setShowDeleteDialog(false);
    setSelectedItemToDelete(null);
  };

  const cancelDelete = () => {
    setShowDeleteDialog(false);
    setSelectedItemToDelete(null);
  };

  // PDF and Excel handlers remain the same
  const handlePDFDownload = () => {
    const doc = new jsPDF("p", "mm", "a4");
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 10;
    let yPosition = 20;

    doc.setFontSize(18);
    doc.setFont("helvetica", "bold");
    doc.text("Balance Sheet Report", pageWidth / 2, yPosition, {
      align: "center",
    });
    yPosition += 10;

    if (fromDate && toDate) {
      doc.setFontSize(12);
      doc.setFont("helvetica", "normal");
      doc.text(`Period: ${fromDate} to ${toDate}`, pageWidth / 2, yPosition, {
        align: "center",
      });
      yPosition += 8;
    }

    if (nameFilter && nameFilter !== "") {
      doc.text(`Agent: ${nameFilter}`, pageWidth / 2, yPosition, {
        align: "center",
      });
      yPosition += 8;
    }

    if (typeFilter !== "All") {
      doc.text(`Type: ${typeFilter}`, pageWidth / 2, yPosition, {
        align: "center",
      });
      yPosition += 8;
    }

    yPosition += 5;

    const tableColumn = [
      { header: "S.No", dataKey: "sno" },
      { header: "Date", dataKey: "date" },
      { header: "Description", dataKey: "description" },
    ];
    if (showCredit) {
      tableColumn.push({ header: "Credit (Rs.)", dataKey: "credit" });
    }
    if (showDebit) {
      tableColumn.push({ header: "Debit (Rs.)", dataKey: "debit" });
    }

    const tableRows = [];
    groupedBalanceSheet.forEach((item, index) => {
      const summaryRow = {
        sno: index + 1,
        date: item.date,
        description: "Daily Summary",
      };
      if (showCredit) {
        summaryRow.credit = item.credit_total.toFixed(2);
      }
      if (showDebit) {
        summaryRow.debit = item.debit_total.toFixed(2);
      }
      tableRows.push(summaryRow);

      item.details.forEach((detail) => {
        const detailRow = {
          sno: "",
          date: "",
          description: detail.description,
        };
        if (showCredit) {
          detailRow.credit = detail.credit.toFixed(2);
        }
        if (showDebit) {
          detailRow.debit = detail.debit.toFixed(2);
        }
        tableRows.push(detailRow);
      });
    });

    const totalRow = {
      sno: "Total",
      date: "",
      description: "",
    };
    if (showCredit) {
      totalRow.credit = totals.paid.toFixed(2);
    }
    if (showDebit) {
      totalRow.debit = totals.debit.toFixed(2);
    }
    tableRows.push(totalRow);

    autoTable(doc, {
      startY: yPosition,
      headStyles: {
        fillColor: [41, 128, 185],
        textColor: 255,
        fontStyle: "bold",
        halign: "center",
      },
      alternateRowStyles: {
        fillColor: [245, 245, 245],
      },
      columnStyles: {
        sno: { halign: "center", cellWidth: 10 },
        date: { halign: "center", cellWidth: 18 },
        description: {
          halign: "left",
          cellWidth: 70,
          overflow: "linebreak",
        },
      },
      styles: {
        fontSize: 7,
        cellPadding: 2,
        valign: "middle",
        lineColor: [0, 0, 0],
        lineWidth: 0.1,
        overflow: "linebreak",
      },
      rowStyles: {
        0: { fillColor: [41, 128, 185], textColor: 255 },
      },
      footStyles: {
        fillColor: [41, 128, 185],
        textColor: 255,
        fontStyle: "bold",
      },
      columns: tableColumn,
      body: tableRows,
      theme: "grid",
      margin: { left: margin, right: margin },
      tableWidth: "auto",
    });

    yPosition = doc.lastAutoTable.finalY + 10;
    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.text(
      `Net Balance: Rs. ${totals.balance.toFixed(2)}`,
      pageWidth / 2,
      yPosition,
      { align: "center" }
    );

    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setFont("helvetica", "italic");
      doc.text(
        `Generated on: ${new Date().toLocaleDateString()} | Page ${i} of ${pageCount}`,
        pageWidth / 2,
        doc.internal.pageSize.getHeight() - 10,
        { align: "center" }
      );
    }

    doc.save(`balance-sheet-${new Date().toISOString().split("T")[0]}.pdf`);
  };

  const handleExcelDownload = () => {
    const wsData = [
      ["Balance Sheet Report"],
      [],
      ...(fromDate && toDate ? [[`Period: ${fromDate} to ${toDate}`]] : []),
      ...(nameFilter && nameFilter !== "" ? [[`Agent: ${nameFilter}`]] : []),
      ...(typeFilter !== "All" ? [[`Type: ${typeFilter}`]] : []),
      [],
      ["S.No", "Date", "Description"],
    ];

    if (showCredit) {
      wsData[wsData.length - 1].push("Credit (Rs.)");
    }
    if (showDebit) {
      wsData[wsData.length - 1].push("Debit (Rs.)");
    }

    groupedBalanceSheet.forEach((item, index) => {
      const summaryRow = [index + 1, item.date, "Daily Summary"];
      if (showCredit) {
        summaryRow.push(item.credit_total.toFixed(2));
      }
      if (showDebit) {
        summaryRow.push(item.debit_total.toFixed(2));
      }
      wsData.push(summaryRow);

      item.details.forEach((detail) => {
        const detailRow = ["", "", detail.description];
        if (showCredit) {
          detailRow.push(detail.credit.toFixed(2));
        }
        if (showDebit) {
          detailRow.push(detail.debit.toFixed(2));
        }
        wsData.push(detailRow);
      });

      wsData.push([]);
    });

    const totalRow = ["Total", "", ""];
    if (showCredit) {
      totalRow.push(totals.paid.toFixed(2));
    }
    if (showDebit) {
      totalRow.push(totals.debit.toFixed(2));
    }
    wsData.push(totalRow);

    wsData.push([]);
    const balanceRow = ["Net Balance", "", "", ""];
    if (showCredit) {
      balanceRow.push("");
    }
    if (showDebit) {
      balanceRow.push(totals.balance.toFixed(2));
    } else {
      balanceRow[3] = totals.balance.toFixed(2);
    }
    wsData.push(balanceRow);

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(wsData);

    const colWidths = [{ wch: 8 }, { wch: 12 }, { wch: 50 }];
    if (showCredit) {
      colWidths.push({ wch: 15 });
    }
    if (showDebit) {
      colWidths.push({ wch: 15 });
    }
    ws["!cols"] = colWidths;

    const headerRange = XLSX.utils.decode_range(ws["!ref"]);
    for (let C = headerRange.s.c; C <= headerRange.e.c; ++C) {
      const cellAddress = { c: C, r: 6 };
      const cellRef = XLSX.utils.encode_cell(cellAddress);
      if (!ws[cellRef]) ws[cellRef] = {};
      ws[cellRef].s = {
        font: { bold: true },
        alignment: { horizontal: "center" },
      };
    }

    wsData.slice(7).forEach((row, rowIndex) => {
      row.forEach((cell, colIndex) => {
        const amountColStart = 3;
        if (
          colIndex >= amountColStart &&
          typeof cell === "string" &&
          !isNaN(cell)
        ) {
          const cellAddress = { c: colIndex, r: rowIndex + 7 };
          const cellRef = XLSX.utils.encode_cell(cellAddress);
          if (!ws[cellRef]) ws[cellRef] = {};
          ws[cellRef].t = "n";
          ws[cellRef].z = "#,##0.00";
          ws[cellRef].s = {
            numFmt: "#,##0.00",
            alignment: { horizontal: "center" },
          };
        }
      });
    });

    const titleCell = { c: 0, r: 0 };
    const titleRef = XLSX.utils.encode_cell(titleCell);
    ws[titleRef].s = {
      font: { bold: true, sz: 16 },
      alignment: { horizontal: "center" },
    };

    XLSX.utils.book_append_sheet(wb, ws, "Balance Sheet");
    XLSX.writeFile(
      wb,
      `balance-sheet-${new Date().toISOString().split("T")[0]}.xlsx`
    );
  };

  const toggleRow = (date) => {
    const newOpenRows = new Set(openRows);
    if (newOpenRows.has(date)) {
      newOpenRows.delete(date);
    } else {
      newOpenRows.add(date);
    }
    setOpenRows(newOpenRows);
  };

  const handleDateChange = (type, date) => {
    if (type === "from") {
      setFromDate(date);
    } else {
      setToDate(date);
    }
  };

  const renderActionButtons = (detail) => {
    if (detail.type !== "credit") {
      return <span className="text-muted"> - </span>;
    }
    return (
      <>
        <Button
          variant="outline-primary"
          size="sm"
          className="me-1"
          onClick={() => handleEdit(detail)}
        >
          Edit
        </Button>
        <Button
          variant="outline-danger"
          size="sm"
          onClick={() => handleDelete(detail)}
        >
          Delete
        </Button>
      </>
    );
  };

  return (
    <div id="main">
      <Container fluid>
        <Row>
          <Col lg="6" md="12" xs="12" className="py-3 align-self-center">
            <PageTitle PageTitle="Balance Sheet" showButton={false} />
          </Col>
          <Col lg="6" md="12" xs="12" className="py-3">
            <div className="d-flex justify-content-end flex-wrap gap-2">
              <Buttons
                label="PDF"
                classname="crud-btn"
                OnClick={handlePDFDownload}
              />
              <Buttons
                label="Excel"
                classname="crud-btn"
                OnClick={handleExcelDownload}
              />
              <Buttons
                label="Add New"
                classname="crud-btn"
                OnClick={handleAdd}
              />
            </div>
          </Col>
          <Col lg="6" className="py-3">
            <div className="content-box">
              <Row>
                <Col lg="12">
                  <div className="d-flex justify-content-between align-items-center p-3 bg-light rounded">
                    <div>
                      <h5 className="mb-1">Balance</h5>
                      <p
                        className={`fw-bold mb-0 ${
                          totals.balance >= 0 ? "text-success" : "text-danger"
                        }`}
                      >
                        Rs. {totals.balance.toFixed(2)}
                      </p>
                    </div>
                  </div>
                </Col>
              </Row>
            </div>
          </Col>
          <Row className="mb-3">
            <Col lg="3" className="py-3">
              <DropDown
                textlabel="Filter by Agent Name"
                options={[
                  { label: "All Agents", value: "" },
                  ...agent.map((a) => ({
                    label: a.agent_name,
                    value: a.agent_name,
                  })),
                ]}
                value={nameFilter}
                onChange={(e) => setNameFilter(e.target.value)}
              />
            </Col>
            <Col lg="3" className="py-3">
              <DropDown
                textlabel="Filter by Type"
                options={[
                  { label: "All", value: "All" },
                  { label: "Credit", value: "Credit" },
                  { label: "Debit", value: "Debit" },
                ]}
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
              />
            </Col>
            <Col lg="3" className="py-3">
              <Calender
                textlabel="From Date"
                value={fromDate}
                onChange={(date) => handleDateChange("from", date)}
              />
            </Col>
            <Col lg="3" className="py-3">
              <Calender
                textlabel="To Date"
                value={toDate}
                onChange={(date) => handleDateChange("to", date)}
              />
            </Col>
          </Row>
          <Col lg="12" xs="12">
            <div className="content-box">
              <Table striped bordered hover responsive className="table-modern">
                <thead className="table-dark">
                  <tr>
                    <th>S.No</th>
                    <th>Date</th>
                    {showCredit && <th>Credit</th>}
                    {showDebit && <th>Debit</th>}
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {groupedBalanceSheet.map((item, index) => (
                    <>
                      <tr key={item.date + "_main"} className="table-row-hover">
                        <td>{index + 1}</td>
                        <td>
                          <DateFormat date={item.date} />
                        </td>
                        {showCredit && (
                          <td className="text-success fw-bold">
                            Rs. {item.credit_total.toFixed(2)}
                          </td>
                        )}
                        {showDebit && (
                          <td className="text-danger fw-bold">
                            Rs. {item.debit_total.toFixed(2)}
                          </td>
                        )}
                        <td>
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => toggleRow(item.date)}
                            className="btn-modern"
                            aria-expanded={openRows.has(item.date)}
                            aria-controls={`details-${item.date}`}
                          >
                            {openRows.has(item.date)
                              ? "Hide Details"
                              : "View Details"}
                          </Button>
                        </td>
                      </tr>
                      <tr key={item.date + "_details"}>
                        <td colSpan={mainCols} className="p-0 border-0">
                          <Collapse in={openRows.has(item.date)}>
                            <div
                              id={`details-${item.date}`}
                              className="bg-light p-3"
                            >
                              <Table
                                bordered
                                size="sm"
                                className="mb-0 details-table align-middle"
                                responsive
                              >
                                <thead className="table-light">
                                  <tr>
                                    <th className="text-center align-middle">
                                      Agent Name
                                    </th>
                                    <th className="text-center align-middle">
                                      Description
                                    </th>
                                    {showCredit && (
                                      <th className="text-center align-middle">
                                        Credit
                                      </th>
                                    )}
                                    {showDebit && (
                                      <th className="text-center align-middle">
                                        Debit
                                      </th>
                                    )}
                                    <th className="text-center align-middle">
                                      Action
                                    </th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {item.details.map((detail, dIndex) => (
                                    <tr key={dIndex} className="align-middle">
                                      <td className="fw-medium text-start ps-3">
                                        {detail.agent_name}
                                      </td>
                                      <td className="fw-medium text-start ps-3">
                                        {detail.description}
                                      </td>
                                      {showCredit && (
                                        <td className="text-success text-center fw-semibold">
                                          Rs. {detail.credit.toFixed(2)}
                                        </td>
                                      )}
                                      {showDebit && (
                                        <td className="text-danger text-center fw-semibold">
                                          Rs. {detail.debit.toFixed(2)}
                                        </td>
                                      )}
                                      <td className="text-center">
                                        {renderActionButtons(detail)}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </Table>
                            </div>
                          </Collapse>
                        </td>
                      </tr>
                    </>
                  ))}
                  {groupedBalanceSheet.length === 0 && (
                    <tr>
                      <td
                        colSpan={mainCols}
                        className="text-center py-4 text-muted"
                      >
                        No entries found for the selected date range.
                      </td>
                    </tr>
                  )}
                </tbody>
                <tfoot>
                  <tr className="table-dark fw-bold">
                    <td colSpan={2} className="text-end">
                      Total
                    </td>
                    {showCredit && (
                      <td className="text-success">
                        Rs. {totals.paid.toFixed(2)}
                      </td>
                    )}
                    {showDebit && (
                      <td className="text-danger">
                        Rs. {totals.debit.toFixed(2)}
                      </td>
                    )}
                    <td></td>
                  </tr>
                </tfoot>
              </Table>
            </div>
          </Col>
        </Row>
      </Container>
      <Dialog
        DialogTitle="Confirm Delete"
        DialogBody="Are you sure you want to delete this balance sheet entry? This action cannot be undone."
        isVisible={showDeleteDialog}
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
        confirmText="Delete"
        cancelText="Cancel"
      />
    </div>
  );
};

export default BalanceSheet;
